// Utility functions for code processing
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { getBuiltinIgnorePatternsForLanguage } from './builtinLanguageIgnorePatterns';

// Default ignore keywords
export const DEFAULT_IGNORE_KEYWORDS = ['TODO', 'FIXME', 'DEBUG', 'REMEMBER'];

// Language-specific comment patterns for stripping comments
export const LANGUAGE_COMMENT_PATTERNS = {
  'JavaScript': {
    patterns: [
      /\/\/[^\n]*/g,           // Single-line: //
      /\/\*[\s\S]{0,10000}?\*\//g,     // Multi-line: /* */ with limit
      /\/\*\*[\s\S]{0,10000}?\*\//g    // JSDoc: /** */ with limit
    ]
  },
  'Python': {
    patterns: [
      /#[^\n]*/g              // Single-line: # (triple-quoted strings handled separately)
    ]
  },
  'Java': {
    patterns: [
      /\/\/[^\n]*/g,           // Single-line: //
      /\/\*\*[\s\S]{0,10000}?\*\//g,  // Javadoc comments with limit
      /\/\*[\s\S]{0,10000}?\*\//g     // Regular multi-line comments with limit
    ]
  },
  'C++': {
    patterns: [
      /\/\/[^\n]*/g,           // Single-line: //
      /\/\*\*[\s\S]{0,10000}?\*\//g,  // Doxygen comments with limit
      /\/\*[\s\S]{0,10000}?\*\//g     // Regular multi-line comments with limit
    ]
  },
  'Ruby': {
    patterns: [
      /#[^\n]*/g,              // Single-line: #
      /##[^\n]*/g,             // RDoc single-line comments
      /=begin[\s\S]{0,10000}?=end/g   // =begin/=end block comments with limit
    ]
  },
  'PHP': {
    patterns: [
      /\/\/[^\n]*/g,           // Single-line: //
      /#[^\n]*/g,              // Single-line: #
      /\/\*\*[\s\S]{0,10000}?\*\//g,  // PHPDoc comments with limit
      /\/\*[\s\S]{0,10000}?\*\//g     // Regular multi-line comments with limit
    ]
  }
};

const HASH_COMMENT_LANGUAGES = new Set(['Python', 'Ruby', 'PHP']);

/** Languages whose line-by-line study uses // single-line comments (plus block comments via regex). */
const SLASH_COMMENT_LANGUAGES = new Set([
  'JavaScript',
  'Java',
  'C++',
  'PHP',
  'Go',
  'Rust',
  'C#',
  'Swift',
  'Kotlin'
]);

/** Languages that share the same comment regex patterns as JavaScript (// and block comments). */
const COMMENT_PATTERN_ALIASES = {
  Go: 'JavaScript',
  Rust: 'JavaScript',
  'C#': 'JavaScript',
  Swift: 'JavaScript',
  Kotlin: 'JavaScript',
  TypeScript: 'JavaScript'
};

function getCommentPatternKey(language) {
  return COMMENT_PATTERN_ALIASES[language] || language;
}

function getCommentPatterns(language) {
  const key = getCommentPatternKey(language);
  return LANGUAGE_COMMENT_PATTERNS[key]?.patterns || LANGUAGE_COMMENT_PATTERNS.JavaScript.patterns;
}

function usesSlashComments(language) {
  const key = getCommentPatternKey(language);
  return SLASH_COMMENT_LANGUAGES.has(key) || SLASH_COMMENT_LANGUAGES.has(language);
}

function usesHashComments(language) {
  return HASH_COMMENT_LANGUAGES.has(language);
}

const PYTHON_CODE_LINE =
  /^\s*(?:def|class|if|elif|else|for|while|try|except|finally|with|import|from|return|pass|break|continue|raise|assert|del|global|nonlocal|yield|async|await|print|@)\b/;

/**
 * Finds a `#` that starts a comment, ignoring `#` inside string literals.
 * @param {string} line
 * @returns {number}
 */
function findHashOutsideStrings(line) {
  let inSingle = false;
  let inDouble = false;
  let inTripleDouble = false;
  let inTripleSingle = false;

  for (let i = 0; i < line.length; i++) {
    const next3 = line.slice(i, i + 3);

    if (!inSingle && !inDouble && next3 === '"""') {
      inTripleDouble = !inTripleDouble;
      i += 2;
      continue;
    }
    if (!inSingle && !inDouble && next3 === "'''") {
      inTripleSingle = !inTripleSingle;
      i += 2;
      continue;
    }
    if (inTripleDouble || inTripleSingle) continue;

    const c = line[i];
    const prev = i > 0 ? line[i - 1] : '';

    if (c === "'" && !inDouble && prev !== '\\') {
      inSingle = !inSingle;
      continue;
    }
    if (c === '"' && !inSingle && prev !== '\\') {
      inDouble = !inDouble;
      continue;
    }
    if (!inSingle && !inDouble && c === '#') {
      return i;
    }
  }

  return -1;
}

/**
 * True when a line is executable code rather than prose wrapped under a comment.
 * @param {string} line
 * @param {string} language
 * @returns {boolean}
 */
function isLikelyCodeLine(line, language) {
  const trimmed = line.trim();
  if (!trimmed) return false;

  if (usesHashComments(language) && trimmed.startsWith('#')) return false;
  if (usesSlashComments(language) && trimmed.startsWith('//')) return false;

  if (language === 'Python') {
    return PYTHON_CODE_LINE.test(trimmed)
      || /^\s*[a-zA-Z_][\w]*\s*(?:=|\(|\[)/.test(trimmed)
      || /^\s*[\)\]\}]/.test(trimmed);
  }

  if (language === 'Ruby') {
    return /^\s*(?:def|class|module|if|elsif|else|unless|while|until|for|end|return|require|puts|p)\b/.test(trimmed)
      || /^\s*[a-zA-Z_][\w]*\s*(?:=|\(|\[)/.test(trimmed)
      || /^\s*[\)\]\}]/.test(trimmed);
  }

  if (language === 'PHP') {
    return /^\s*(?:function|class|if|else|foreach|while|return|echo|public|private|protected|\$\w)/.test(trimmed)
      || /^\s*[a-zA-Z_][\w]*\s*(?:=|\(|\[)/.test(trimmed)
      || /^\s*[\)\]\}]/.test(trimmed);
  }

  if (language === 'Java') {
    return /^\s*(?:public|private|protected|static|class|interface|if|else|for|while|return|import|package|@|\{|\})/.test(trimmed)
      || /^\s*[a-zA-Z_][\w]*\s*(?:=|\(|\[)/.test(trimmed)
      || /^\s*[\)\]\}]/.test(trimmed);
  }

  if (language === 'C++') {
    return /^\s*(?:#include|using|namespace|class|struct|if|else|for|while|return|int|void|auto|template|\{|\})/.test(trimmed)
      || /^\s*[a-zA-Z_][\w]*\s*(?:=|\(|\[)/.test(trimmed)
      || /^\s*[\)\]\}]/.test(trimmed);
  }

  // JavaScript and C-style aliases (Go, Rust, C#, Swift, Kotlin)
  return /^\s*(?:function|class|if|else|for|while|return|const|let|var|import|export|async|await|switch|case|default|try|catch|throw|func|fn|fun|package|\{|\})/.test(trimmed)
    || /^\s*[a-zA-Z_$][\w$]*\s*(?:=|\(|\[)/.test(trimmed)
    || /^\s*[\)\]\}]/.test(trimmed);
}

/**
 * True when a line is prose continuation of a prior `#` comment (no `#` prefix).
 * @param {string} line
 * @param {string} language
 * @returns {boolean}
 */
function isCommentContinuationLine(line, language) {
  const trimmed = line.trim();
  if (!trimmed) return false;
  if (isLikelyCodeLine(line, language)) return false;

  if (/[,\\(\[\{:=]$/.test(trimmed)) return false;
  if (/^\s*\)/.test(trimmed)) return false;

  const words = trimmed.split(/\s+/);
  if (words.length >= 3 && /^[a-zA-Z0-9\s'".\-]+$/.test(trimmed)) return true;
  if (/^(creating|assigning|here|this|note|when|then|we|you|the|for example|remember|that'?s)\b/i.test(trimmed)) {
    return true;
  }

  return false;
}

function isFullLineHashComment(line) {
  const hashIdx = findHashOutsideStrings(line);
  if (hashIdx === -1) return false;
  return line.slice(0, hashIdx).trim().length === 0;
}

/**
 * Returns the index of the first line after a `#` comment block starting at startIndex.
 * Full-line `#` runs consume consecutive `#` lines; inline `#` only consumes prose wraps.
 * @param {string[]} lines
 * @param {number} startIndex
 * @param {string} language
 * @returns {number}
 */
function advancePastHashCommentBlock(lines, startIndex, language) {
  let j = startIndex + 1;

  if (isFullLineHashComment(lines[startIndex])) {
    while (j < lines.length && isFullLineHashComment(lines[j])) {
      j++;
    }
    return j;
  }

  while (j < lines.length && isCommentContinuationLine(lines[j], language)) {
    j++;
  }

  return j;
}

/**
 * Removes `#` comments and un-prefixed prose lines that continue them.
 * Fixes flashcards where authors wrap long comments across multiple lines.
 * @param {string} code
 * @param {string} language
 * @returns {string}
 */
function preprocessHashCommentLines(code, language) {
  if (!code || !HASH_COMMENT_LANGUAGES.has(language)) return code;

  const lines = code.split('\n');
  const kept = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const hashIdx = findHashOutsideStrings(line);

    if (hashIdx !== -1) {
      const codePart = line.slice(0, hashIdx).trimEnd();
      const j = advancePastHashCommentBlock(lines, i, language);
      i = j - 1;

      if (codePart.trim().length > 0) {
        kept.push(codePart);
      }
      continue;
    }

    kept.push(line);
  }

  return kept.join('\n');
}

/**
 * Collects `#` comment spans including wrapped continuation lines (no `#` on each line).
 * @param {string} code
 * @param {string} language
 * @returns {{ start: number, end: number, text: string }[]}
 */
function collectHashCommentBlocksWithContinuations(code, language) {
  if (!code || !HASH_COMMENT_LANGUAGES.has(language)) return [];

  const lines = code.split('\n');
  const lineStarts = [0];
  for (let idx = 1; idx < lines.length; idx++) {
    lineStarts.push(lineStarts[idx - 1] + lines[idx - 1].length + 1);
  }

  const blocks = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const hashIdx = findHashOutsideStrings(line);

    if (hashIdx !== -1) {
      const j = advancePastHashCommentBlock(lines, i, language);
      const lastLineIdx = Math.max(i, j - 1);
      const textParts = [line.slice(hashIdx)];
      for (let k = i + 1; k < j; k++) {
        textParts.push(lines[k]);
      }

      blocks.push({
        start: lineStarts[i] + hashIdx,
        end: lineStarts[lastLineIdx] + lines[lastLineIdx].length,
        text: textParts.join('\n')
      });

      i = j - 1;
    }
  }

  return blocks;
}

/**
 * Finds `//` starting a comment outside string literals (skips `://` in URLs).
 * @param {string} line
 * @returns {number}
 */
function findDoubleSlashOutsideStrings(line) {
  let inSingle = false;
  let inDouble = false;
  let inTemplate = false;

  for (let i = 0; i < line.length - 1; i++) {
    const c = line[i];
    const next = line[i + 1];
    const prev = i > 0 ? line[i - 1] : '';

    if (!inDouble && !inTemplate && c === "'" && prev !== '\\') {
      inSingle = !inSingle;
      continue;
    }
    if (!inSingle && !inTemplate && c === '"' && prev !== '\\') {
      inDouble = !inDouble;
      continue;
    }
    if (!inSingle && !inDouble && c === '`' && prev !== '\\') {
      inTemplate = !inTemplate;
      continue;
    }
    if (inSingle || inDouble || inTemplate) continue;

    if (c === '/' && next === '/' && prev !== ':') {
      return i;
    }
  }

  return -1;
}

function isFullLineSlashComment(line) {
  const slashIdx = findDoubleSlashOutsideStrings(line);
  if (slashIdx === -1) return false;
  return line.slice(0, slashIdx).trim().length === 0;
}

function advancePastSlashCommentBlock(lines, startIndex, language) {
  let j = startIndex + 1;

  if (isFullLineSlashComment(lines[startIndex])) {
    while (j < lines.length && isFullLineSlashComment(lines[j])) {
      j++;
    }
    return j;
  }

  while (j < lines.length && isCommentContinuationLine(lines[j], language)) {
    j++;
  }

  return j;
}

function preprocessSlashCommentLines(code, language) {
  if (!code || !usesSlashComments(language)) return code;

  const lines = code.split('\n');
  const kept = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const slashIdx = findDoubleSlashOutsideStrings(line);

    if (slashIdx !== -1) {
      const codePart = line.slice(0, slashIdx).trimEnd();
      const j = advancePastSlashCommentBlock(lines, i, language);
      i = j - 1;

      if (codePart.trim().length > 0) {
        kept.push(codePart);
      }
      continue;
    }

    kept.push(line);
  }

  return kept.join('\n');
}

function collectSlashCommentBlocksWithContinuations(code, language) {
  if (!code || !usesSlashComments(language)) return [];

  const lines = code.split('\n');
  const lineStarts = [0];
  for (let idx = 1; idx < lines.length; idx++) {
    lineStarts.push(lineStarts[idx - 1] + lines[idx - 1].length + 1);
  }

  const blocks = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const slashIdx = findDoubleSlashOutsideStrings(line);

    if (slashIdx !== -1) {
      const j = advancePastSlashCommentBlock(lines, i, language);
      const lastLineIdx = Math.max(i, j - 1);
      const textParts = [line.slice(slashIdx)];
      for (let k = i + 1; k < j; k++) {
        textParts.push(lines[k]);
      }

      blocks.push({
        start: lineStarts[i] + slashIdx,
        end: lineStarts[lastLineIdx] + lines[lastLineIdx].length,
        text: textParts.join('\n')
      });

      i = j - 1;
    }
  }

  return blocks;
}

/** Runs hash- and slash-style line comment preprocessing for the resolved language. */
function preprocessLineComments(code, language) {
  if (!code) return code;
  let result = code;
  if (usesHashComments(language)) {
    result = preprocessHashCommentLines(result, language);
  }
  if (usesSlashComments(language)) {
    result = preprocessSlashCommentLines(result, language);
  }
  return result;
}

function shouldSkipCommentRegexPattern(pattern, language) {
  const src = pattern.toString();
  if (usesHashComments(language) && /^\/#/.test(src)) return true;
  if (language === 'Ruby' && /^\/##/.test(src)) return true;
  if (usesSlashComments(language) && /^\/\//.test(src)) return true;
  return false;
}

/** Characters that precede a triple-quoted Python string literal (not a docstring). */
const PYTHON_TRIPLE_STRING_LITERAL_BEFORE = new Set(['=', '(', '[', '{', ',', '@']);

/**
 * True when an opening triple quote starts a docstring rather than a string literal.
 * Docstrings: leading whitespace only on the line, or immediately after `:`.
 * Literals: preceded by `=`, `(`, etc. (including multiline assignment values).
 * @param {string} code
 * @param {number} lineStart
 * @param {number} openIndex - index of the opening triple quote
 * @returns {boolean}
 */
function isPythonTripleQuotedDocstringOpen(code, lineStart, openIndex) {
  let j = openIndex - 1;
  while (j >= lineStart && /\s/.test(code[j])) j--;

  if (j < lineStart) return true;

  const prev = code[j];
  if (PYTHON_TRIPLE_STRING_LITERAL_BEFORE.has(prev)) return false;
  if (prev === ':') return true;

  return code.slice(lineStart, openIndex).trim().length === 0;
}

/**
 * Removes standalone Python docstrings while preserving triple-quoted string literals.
 * @param {string} code
 * @returns {string}
 */
function stripPythonTripleQuotedDocstringsOnly(code) {
  if (!code) return code;

  let result = '';
  let i = 0;
  let lineStart = 0;

  while (i < code.length) {
    const next3 = code.slice(i, i + 3);
    if (next3 === '"""' || next3 === "'''") {
      if (isPythonTripleQuotedDocstringOpen(code, lineStart, i)) {
        const closeQuote = next3;
        i += 3;
        while (i < code.length) {
          if (code.slice(i, i + 3) === closeQuote) {
            i += 3;
            break;
          }
          if (code[i] === '\n') lineStart = i + 1;
          i++;
        }
        if (result.length === 0 && code[i] === '\n') {
          i += 1;
          lineStart = i;
        }
        continue;
      }

      const closeQuote = next3;
      result += closeQuote;
      i += 3;
      while (i < code.length) {
        if (code.slice(i, i + 3) === closeQuote) {
          result += closeQuote;
          i += 3;
          break;
        }
        result += code[i];
        if (code[i] === '\n') lineStart = i + 1;
        i++;
      }
      continue;
    }

    result += code[i];
    if (code[i] === '\n') lineStart = i + 1;
    i++;
  }

  return result;
}

function spansOverlap(a, b) {
  return a.start < b.end && b.start < a.end;
}

// Memoization cache for stripComments function
const stripCommentsCache = new Map();

/**
 * Strips comments from code based on language
 * @param {string} code - The code to strip comments from
 * @param {string} language - The programming language
 * @returns {string} - Code with comments removed
 */
export const stripComments = (code, language = 'JavaScript') => {
  try {
    const resolvedLanguage = resolveCommentProcessingLanguage(code, language);
    const cacheKey = `${resolvedLanguage}:${code}`;

    if (stripCommentsCache.has(cacheKey)) {
      return stripCommentsCache.get(cacheKey);
    }

    const patterns = getCommentPatterns(resolvedLanguage);

    let processed = preprocessLineComments(code, resolvedLanguage);
    if (resolvedLanguage === 'Python') {
      processed = stripPythonTripleQuotedDocstringsOnly(processed);
    }
    const regexPatterns = patterns.filter((pattern) => !shouldSkipCommentRegexPattern(pattern, resolvedLanguage));
    const result = regexPatterns.reduce(
      (processedCode, pattern) => processedCode.replace(pattern, ''),
      processed
    );

    stripCommentsCache.set(cacheKey, result);

    return result;
  } catch (error) {
    console.error('Error in stripComments, using fallback:', error);
    let cleanedCode = code;
    const resolvedLanguage = resolveCommentProcessingLanguage(code, language);
    cleanedCode = preprocessLineComments(cleanedCode, resolvedLanguage);
    const patterns = getCommentPatterns(resolvedLanguage).filter(
      (pattern) => !shouldSkipCommentRegexPattern(pattern, resolvedLanguage)
    );
    if (resolvedLanguage === 'Python') {
      cleanedCode = stripPythonTripleQuotedDocstringsOnly(cleanedCode);
      cleanedCode = patterns.reduce((c, p) => c.replace(p, ''), cleanedCode);
    } else if (usesSlashComments(resolvedLanguage)) {
      cleanedCode = cleanedCode.replace(/\/\*[\s\S]*?\*\//g, '');
    }
    return cleanedCode;
  }
};

/**
 * Collects all regex matches with their start index and full match text.
 * @param {string} code - Source code
 * @param {RegExp} pattern - Pattern (will be cloned to avoid lastIndex issues)
 * @returns {{ start: number, end: number, text: string }[]}
 */
function collectCommentSpans(code, pattern) {
  const spans = [];
  const re = new RegExp(pattern.source, pattern.flags.includes('g') ? pattern.flags : pattern.flags + 'g');
  let m;
  while ((m = re.exec(code)) !== null) {
    spans.push({ start: m.index, end: m.index + m[0].length, text: m[0] });
  }
  return spans;
}

/**
 * Merges overlapping or adjacent comment spans, and spans separated only by whitespace
 * (e.g. consecutive # or // lines), keeping combined extent and concatenated text.
 * @param {{ start: number, end: number, text: string }[]} spans - Sorted by start
 * @param {string} code - Full source so we can check the gap between spans
 * @returns {{ start: number, end: number, text: string }[]}
 */
function mergeCommentSpans(spans, code) {
  if (spans.length === 0) return [];
  const sorted = [...spans].sort((a, b) => a.start - b.start);
  const merged = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const prev = merged[merged.length - 1];
    const curr = sorted[i];
    const gap = code.slice(prev.end, curr.start);
    const overlappingOrAdjacent = curr.start <= prev.end;
    const onlyWhitespaceBetween = /^\s*$/.test(gap);
    if (overlappingOrAdjacent || onlyWhitespaceBetween) {
      prev.end = Math.max(prev.end, curr.end);
      prev.text = (prev.text + '\n' + curr.text).trim();
    } else {
      merged.push({ ...curr });
    }
  }
  return merged;
}

/**
 * Guesses language from code content for hint extraction fallback.
 * Used when the card/deck language yields no hints so we can try the detected language.
 * @param {string} code - Source code
 * @returns {string|null} - One of LANGUAGE_COMMENT_PATTERNS keys, or null
 */
function detectLanguageFromCodeForHints(code) {
  if (!code || code.length < 5) return null;
  const s = code.toLowerCase();

  if (s.includes('<?php')) return 'PHP';
  if (s.includes('=begin') || (/\bdef\b/.test(s) && /\bend\b/.test(s) && (s.includes('puts ') || s.includes('require ')))) {
    return 'Ruby';
  }
  if (s.includes('public class') || s.includes('system.out') || s.includes('public static void main')) {
    return 'Java';
  }
  if (s.includes('#include') || s.includes('std::') || s.includes('using namespace')) {
    return 'C++';
  }
  if (/^\s*package\s+[a-z0-9_.]+/im.test(code) && /\bfunc\s+/.test(code)) {
    return 'Go';
  }
  if (/\bfn\s+[a-z0-9_]+/i.test(code) || s.includes('println!') || s.includes('let mut ')) {
    return 'Rust';
  }
  if (
    s.includes('def ')
    || s.includes('print(')
    || s.includes('"""')
    || s.includes("'''")
    || (/^\s*import\s+/m.test(code) && s.includes('from '))
    || /^\s*#/m.test(code)
    || /#\s*[^\n]/.test(code)
  ) {
    return 'Python';
  }
  if (
    s.includes('function ')
    || s.includes('const ')
    || s.includes('let ')
    || s.includes('=>')
    || s.includes('console.log')
    || /^\s*\/\//m.test(code)
  ) {
    return 'JavaScript';
  }
  if (s.includes('//') && s.includes('$')) return 'PHP';
  return null;
}

/**
 * Maps deck/card language strings to LANGUAGE_COMMENT_PATTERNS keys.
 * @param {string} language
 * @returns {string|null}
 */
function normalizeCommentProcessingLanguage(language) {
  if (!language) return null;
  const raw = String(language).trim();
  const l = raw.toLowerCase();
  if (l.includes('python') || l === 'py') return 'Python';
  if (l.includes('javascript') || l === 'js') return 'JavaScript';
  if (l.includes('typescript') || l === 'ts') return 'TypeScript';
  if (l.includes('java') && !l.includes('javascript')) return 'Java';
  if (l.includes('c++') || l === 'cpp' || l.includes('c / c++') || l === 'c') return 'C++';
  if (l.includes('ruby') || l === 'rb') return 'Ruby';
  if (l.includes('php')) return 'PHP';
  if (l.includes('csharp') || l === 'cs' || l.includes('c#')) return 'C#';
  if (l.includes('golang') || l === 'go') return 'Go';
  if (l.includes('rust') || l === 'rs') return 'Rust';
  if (l.includes('swift')) return 'Swift';
  if (l.includes('kotlin') || l === 'kt') return 'Kotlin';
  if (LANGUAGE_COMMENT_PATTERNS[raw]) return raw;
  if (COMMENT_PATTERN_ALIASES[raw]) return raw;
  return null;
}

/**
 * Picks the language used for comment stripping / line splitting.
 * Falls back to code detection when metadata is missing or still set to JavaScript.
 * @param {string} code
 * @param {string} language
 * @returns {string}
 */
function resolveCommentProcessingLanguage(code, language = 'JavaScript') {
  const normalized = normalizeCommentProcessingLanguage(language);
  let resolved = normalized || 'JavaScript';
  if (!code) return resolved;

  const detected = detectLanguageFromCodeForHints(code);
  if (!detected) return resolved;
  if (!normalized) return detected;

  if (resolved === 'JavaScript' && detected !== 'JavaScript') {
    return detected;
  }

  return resolved;
}

/**
 * Extracts comment blocks from code and the number of code lines following each comment
 * (until the next comment or end of file). Uses same patterns as stripComments so
 * "code lines" align with splitCodeIntoLines. If the given language yields no hints,
 * tries code-based language detection so cookbooks with missing/wrong language still get hints.
 * @param {string} code - Raw solution code (with comments)
 * @param {string} language - Programming language
 * @returns {{ text: string, linesAfter: number, startLineIndex: number }[]} - Hints in order; startLineIndex is the index in the code-lines array of the first line after this comment
 */
export const extractCommentHints = (code, language = 'JavaScript') => {
  if (!code || typeof code !== 'string') return [];

  try {
    const effectiveLanguage = resolveCommentProcessingLanguage(code, language);
    const patterns = getCommentPatterns(effectiveLanguage);

    let allSpans = [];
    if (usesHashComments(effectiveLanguage)) {
      allSpans.push(...collectHashCommentBlocksWithContinuations(code, effectiveLanguage));
    }
    if (usesSlashComments(effectiveLanguage)) {
      allSpans.push(...collectSlashCommentBlocksWithContinuations(code, effectiveLanguage));
    }
    patterns.forEach(pattern => {
      if (shouldSkipCommentRegexPattern(pattern, effectiveLanguage)) {
        return;
      }
      const regexSpans = collectCommentSpans(code, pattern).filter(
        (span) => !allSpans.some((existing) => spansOverlap(existing, span))
      );
      allSpans.push(...regexSpans);
    });
    const merged = mergeCommentSpans(allSpans, code);

    if (merged.length === 0) return [];

    let codeLineIndex = 0;
    const hints = [];

    for (let i = 0; i < merged.length; i++) {
      const comment = merged[i];
      const nextStart = i + 1 < merged.length ? merged[i + 1].start : code.length;
      const segment = code.slice(comment.end, nextStart);
      const segmentLines = splitCodeIntoLines(segment, effectiveLanguage);
      const linesAfter = segmentLines.length;

      let text = comment.text.trim();
      // Normalize for display: strip comment markers so hint reads as plain text
      if (usesHashComments(effectiveLanguage)) {
        text = text.split('\n').map(line => line.replace(/^\s*#\s?/, '').replace(/^\s*##\s?/, '')).join('\n').trim();
      }
      if (usesSlashComments(effectiveLanguage)) {
        text = text.split('\n').map(line => line.replace(/^\s*\/\/\s?/, '')).join('\n').trim();
        text = text.replace(/^\/\*\*?|\*\/\s*$/g, '').trim();
      }
      if (effectiveLanguage === 'Python') {
        text = text.replace(/^["']{3}\s*|["']{3}\s*$/g, '').trim();
      }
      text = text.replace(/\n\s+/g, ' ').replace(/\s+/g, ' ').trim();
      if (text.length > 0) {
        hints.push({
          text,
          linesAfter,
          startLineIndex: codeLineIndex
        });
      }
      codeLineIndex += linesAfter;
    }

    return hints;
  } catch (err) {
    console.error('extractCommentHints error:', err);
    return [];
  }
};

const PYTHON_ASSIGNMENT_SKIP =
  /^\s*(def|class|import|from|if|elif|else|for|while|with|return|pass|break|continue|raise|try|except|finally|async|await|del|global|nonlocal|print)\b/;

const RUBY_ASSIGNMENT_SKIP =
  /^\s*(def|class|module|if|elsif|else|unless|while|until|for|end|return|require|puts|p|raise|begin|rescue|ensure|do|case|when|break|next|redo|retry|alias|undef|defined\?|super|yield)\b/;

/** Type fragments in typed declarations (Java, C++, C#, Go var with type, etc.). */
const TYPED_DECL_TYPE_TOKEN = '[\\w.:<>\\[\\]*&]+';

const JS_STYLE_DECL_RE =
  /^(?:let|const|var)\s+(\w+)(?:\s*:\s*[^=]+)?\s*=\s*(.+?)(?:;?\s*)$/;

const LET_VAL_DECL_RE =
  /^(?:let|val)\s+(\w+)(?:\s*:\s*[^=]+)?\s*=\s*(.+?)(?:;?\s*)$/;

const RUST_LET_DECL_RE =
  /^let\s+(?:mut\s+)?(\w+)\s*=\s*(.+?)(?:;?\s*)$/;

const GO_SHORT_DECL_RE =
  /^(\w+)\s*:=\s*(.+?)(?:;?\s*)$/;

const GO_VAR_DECL_RE =
  /^var\s+(\w+)(?:\s+[\w.*\[\]]+)?\s*=\s*(.+?)(?:;?\s*)$/;

const PHP_VAR_DECL_RE =
  /^\$(\w+)\s*=\s*(.+?)(?:;?\s*)$/;

const TYPED_DECL_RE = new RegExp(
  `^(?:(?:final|const)\\s+)?(?:(?:${TYPED_DECL_TYPE_TOKEN})\\s+)+(\\w+)\\s*=\\s*(.+?)(?:;?\\s*)$`
);

function pushAssignment(results, name, value, { prefix = '' } = {}) {
  const bareName = (name || '').trim();
  if (!bareName || !/^\w+$/.test(bareName)) return;
  results.push({
    name: `${prefix}${bareName}`,
    value: truncateVariableValue(value)
  });
}

function parsePythonAssignments(trimmed, results) {
  if (PYTHON_ASSIGNMENT_SKIP.test(trimmed)) return true;

  const tupleMatch = trimmed.match(
    /^([a-zA-Z_]\w*(?:\s*,\s*[a-zA-Z_]\w*)+)\s*=\s*(.+)$/
  );
  if (tupleMatch) {
    const names = tupleMatch[1].split(',').map((name) => name.trim()).filter(Boolean);
    const values = splitTupleRhs(tupleMatch[2]);
    names.forEach((name, index) => {
      pushAssignment(results, name, values[index] || '…');
    });
    return true;
  }

  const pyMatch = trimmed.match(/^([a-zA-Z_]\w*)\s*=\s*(.+)$/);
  if (pyMatch) {
    pushAssignment(results, pyMatch[1], pyMatch[2]);
  }
  return true;
}

function parseRubyAssignments(trimmed, results) {
  if (RUBY_ASSIGNMENT_SKIP.test(trimmed)) return true;

  const rubyMatch = trimmed.match(/^([a-zA-Z_]\w*)\s*=\s*(.+)$/);
  if (rubyMatch) {
    pushAssignment(results, rubyMatch[1], rubyMatch[2]);
  }
  return true;
}

function parsePhpAssignments(trimmed, results) {
  const phpMatch = trimmed.match(PHP_VAR_DECL_RE);
  if (phpMatch) {
    pushAssignment(results, phpMatch[1], phpMatch[2], { prefix: '$' });
    return true;
  }
  return false;
}

function parseGoAssignments(trimmed, results) {
  const shortMatch = trimmed.match(GO_SHORT_DECL_RE);
  if (shortMatch) {
    pushAssignment(results, shortMatch[1], shortMatch[2]);
    return true;
  }

  const varMatch = trimmed.match(GO_VAR_DECL_RE);
  if (varMatch) {
    pushAssignment(results, varMatch[1], varMatch[2]);
    return true;
  }

  return false;
}

function truncateVariableValue(value, maxLen = 80) {
  const trimmed = (value || '').trim();
  if (trimmed.length <= maxLen) return trimmed;
  return `${trimmed.slice(0, maxLen - 1)}…`;
}

function splitTupleRhs(rhs) {
  const values = [];
  let current = '';
  let depth = 0;
  let inString = null;

  for (let i = 0; i < rhs.length; i += 1) {
    const ch = rhs[i];
    if (inString) {
      current += ch;
      if (ch === inString && rhs[i - 1] !== '\\') inString = null;
      continue;
    }
    if (ch === '"' || ch === "'") {
      inString = ch;
      current += ch;
      continue;
    }
    if (ch === '(' || ch === '[' || ch === '{') depth += 1;
    if (ch === ')' || ch === ']' || ch === '}') depth -= 1;
    if (ch === ',' && depth === 0) {
      values.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }

  if (current.trim()) values.push(current.trim());
  return values;
}

/**
 * Parses variable assignments from a single study line.
 * @param {string} line - One code line (comments already stripped)
 * @param {string} language - Programming language
 * @returns {{ name: string, value: string }[]}
 */
function parseAssignmentsFromLine(line, language) {
  const results = [];
  const trimmed = (line || '').trim();
  if (!trimmed) return results;

  const resolvedLanguage = normalizeCommentProcessingLanguage(language) || 'JavaScript';

  if (resolvedLanguage === 'Python') {
    parsePythonAssignments(trimmed, results);
    return results;
  }

  if (resolvedLanguage === 'Ruby') {
    parseRubyAssignments(trimmed, results);
    return results;
  }

  if (resolvedLanguage === 'PHP' && parsePhpAssignments(trimmed, results)) {
    return results;
  }

  if (resolvedLanguage === 'Go' && parseGoAssignments(trimmed, results)) {
    return results;
  }

  const rustMatch = trimmed.match(RUST_LET_DECL_RE);
  if (rustMatch) {
    pushAssignment(results, rustMatch[1], rustMatch[2]);
    return results;
  }

  const jsMatch = trimmed.match(JS_STYLE_DECL_RE);
  if (jsMatch) {
    pushAssignment(results, jsMatch[1], jsMatch[2]);
    return results;
  }

  const letValMatch = trimmed.match(LET_VAL_DECL_RE);
  if (letValMatch) {
    pushAssignment(results, letValMatch[1], letValMatch[2]);
    return results;
  }

  const typedMatch = trimmed.match(TYPED_DECL_RE);
  if (typedMatch) {
    pushAssignment(results, typedMatch[1], typedMatch[2]);
  }

  return results;
}

/**
 * Extracts variable assignments from study code lines.
 * @param {string[]} codeLines - Lines returned by splitCodeIntoLines
 * @param {string} language - Programming language
 * @returns {{ name: string, value: string, lineIndex: number }[]}
 */
export const extractVariableHintsFromLines = (codeLines, language = 'JavaScript') => {
  if (!Array.isArray(codeLines) || codeLines.length === 0) return [];

  const hints = [];
  const seen = new Set();

  codeLines.forEach((line, lineIndex) => {
    const assignments = parseAssignmentsFromLine(line, language);
    assignments.forEach(({ name, value }) => {
      if (seen.has(name)) return;
      seen.add(name);
      hints.push({ name, value, lineIndex });
    });
  });

  return hints;
};

/**
 * Extracts variable assignments from raw solution code for study hints.
 * Monaco/editor autocomplete does not expose runtime values — this parses
 * the reference solution statically, aligned with splitCodeIntoLines indices.
 * @param {string} code - Raw solution code
 * @param {string} language - Programming language
 * @returns {{ name: string, value: string, lineIndex: number }[]}
 */
export const extractVariableHints = (code, language = 'JavaScript') => {
  if (!code || typeof code !== 'string') return [];

  try {
    const effectiveLanguage = resolveCommentProcessingLanguage(code, language);
    const codeLines = splitCodeIntoLines(code, effectiveLanguage);
    return extractVariableHintsFromLines(codeLines, effectiveLanguage);
  } catch (err) {
    console.error('extractVariableHints error:', err);
    return [];
  }
};

// Memoization cache for normalizeCode function
const normalizeCodeCache = new Map();

/**
 * Normalizes code for comparison by removing whitespace, comments, etc.
 * @param {string} code - The code to normalize
 * @param {string} language - The programming language
 * @returns {string} - Normalized code for comparison
 */
export const normalizeCode = (code, language = 'JavaScript') => {
  try {
    const resolvedLanguage = resolveCommentProcessingLanguage(code, language);
    const cacheKey = `${resolvedLanguage}:${code}`;

    if (normalizeCodeCache.has(cacheKey)) {
      return normalizeCodeCache.get(cacheKey);
    }

    const result = stripComments(code, resolvedLanguage)
      .replace(/\s+/g, '')
      .replace(/function\s+([a-zA-Z_$][a-zA-Z0-9_$]*)/g, 'function')
      .replace(/(?:let|const|var)\s+([a-zA-Z_$][a-zA-Z0-9_$]*)/g, 'let')
      .replace(/;/g, '')
      .replace(/\(\)/g, '')
      .toLowerCase();

    // Store result in cache
    normalizeCodeCache.set(cacheKey, result);

    return result;
  } catch (error) {
    console.error('Error in normalizeCode, using fallback:', error);
    return simpleNormalizeCode(code, language);
  }
};

/**
 * Retrieves user's ignore patterns for a specific language from Firestore or uses defaults
 * @param {string} userId - The user ID
 * @param {string} language - The programming language
 * @returns {Promise<Array>} - Array of ignore patterns for the specified language
 */
const simplePatternsFromKeywords = keywords =>
  keywords.map(keyword => ({
    type: 'simple',
    pattern: keyword,
    caseSensitive: false,
    enabled: true
  }));

export const getIgnorePatterns = async (userId, language = 'JavaScript') => {
  const builtin = getBuiltinIgnorePatternsForLanguage(language);

  try {
    if (!userId) {
      console.log('No userId provided, using built-in patterns for language:', language);
      return builtin;
    }

    console.log('Fetching ignore patterns for user:', userId, 'language:', language);

    // Check if Firebase is available (handle CORS issues)
    if (typeof db === 'undefined' || !db) {
      console.warn('Firebase not available, using built-in patterns');
      return builtin;
    }

    const userSettingsRef = doc(db, `users/${userId}/settings/codeProcessing`);
    const settingsDoc = await getDoc(userSettingsRef);

    if (settingsDoc.exists()) {
      const data = settingsDoc.data();
      console.log('Found user settings:', data);

      // Check if we have language-specific patterns
      if (data.languagePatterns && data.languagePatterns[language]) {
        return data.languagePatterns[language];
      }

      // Check if we have language-specific keywords (old format)
      if (data.languageKeywords && data.languageKeywords[language]) {
        return simplePatternsFromKeywords(data.languageKeywords[language]);
      }

      // Global legacy list only when no per-language pattern map is stored
      if (
        data.ignoreKeywords &&
        (!data.languagePatterns || Object.keys(data.languagePatterns).length === 0)
      ) {
        return simplePatternsFromKeywords(data.ignoreKeywords);
      }

      // User has other languages saved but not this one — use wave-1 built-ins
      if (data.languagePatterns && Object.keys(data.languagePatterns).length > 0) {
        return builtin;
      }
    }

    console.log('No user settings found, using built-in patterns');
    return builtin;
  } catch (error) {
    console.error('Error fetching ignore patterns:', error);
    return builtin;
  }
};

/**
 * For backward compatibility - converts new pattern objects to old keyword strings
 * @param {Array} patterns - Array of pattern objects
 * @returns {string[]} - Array of keyword strings
 */
export const patternsToKeywords = (patterns = []) => {
  if (!patterns || patterns.length === 0) {
    return DEFAULT_IGNORE_KEYWORDS;
  }

  // If patterns is already an array of strings, return it
  if (typeof patterns[0] === 'string') {
    return patterns;
  }

  // Extract patterns from pattern objects
  return patterns
    .filter(p => p.enabled !== false) // Skip disabled patterns
    .map(p => {
      if (p.type === 'simple') return p.pattern;
      if (p.type === 'comment') return p.pattern;
      if (p.type === 'multiline') return `${p.openPattern}...${p.closePattern}`;
      return null;
    })
    .filter(Boolean); // Remove null values
};

/**
 * Checks if a line contains any of the ignore patterns
 * @param {string} line - The line to check
 * @param {Array} patterns - Array of pattern objects to check against
 * @returns {boolean} - True if the line matches any pattern
 */
export const lineMatchesPattern = (line, patterns = []) => {
  if (!line || !patterns || patterns.length === 0) return false;

  // Convert patterns to old format if needed
  const patternObjects = Array.isArray(patterns) && typeof patterns[0] === 'string'
    ? patterns.map(p => ({ type: 'simple', pattern: p, caseSensitive: false, enabled: true }))
    : patterns;

  // Filter out disabled patterns
  const enabledPatterns = patternObjects.filter(p => p.enabled !== false);

  // Check if line matches any pattern
  return enabledPatterns.some(pattern => {
    // Skip multiline patterns for single line checks
    if (pattern.type === 'multiline') return false;

    const lineToCheck = pattern.caseSensitive ? line : line.toLowerCase();
    const patternToMatch = pattern.caseSensitive ? pattern.pattern : pattern.pattern.toLowerCase();

    return lineToCheck.includes(patternToMatch);
  });
};

/**
 * For backward compatibility
 * @param {string} line - The line to check
 * @param {Array} ignoreKeywords - Array of keywords or pattern objects
 * @returns {boolean} - True if the line matches any pattern
 */
export const lineContainsIgnoreKeyword = (line, ignoreKeywords = DEFAULT_IGNORE_KEYWORDS) => {
  // Convert to pattern objects if needed
  const patterns = Array.isArray(ignoreKeywords) && typeof ignoreKeywords[0] === 'string'
    ? ignoreKeywords.map(k => ({ type: 'simple', pattern: k, caseSensitive: false, enabled: true }))
    : ignoreKeywords;

  return lineMatchesPattern(line, patterns);
};

/**
 * Pre-processes code to handle multiline patterns
 * @param {string} code - The code to process
 * @param {Array} patterns - Array of pattern objects
 * @returns {string} - Code with multiline sections removed
 */
export const preprocessMultilinePatterns = (code, patterns = []) => {
  if (!code) return '';

  // Extract multiline patterns
  const multilinePatterns = patterns.filter(p => p.type === 'multiline' && p.enabled !== false);
  if (multilinePatterns.length === 0) return code;

  let processedCode = code;

  // Process each multiline pattern
  multilinePatterns.forEach(pattern => {
    const { openPattern, closePattern, caseSensitive } = pattern;

    // Split the code into chunks to avoid ReDoS
    const chunks = splitIntoChunks(processedCode, 1000);
    processedCode = '';

    // Process each chunk separately
    for (const chunk of chunks) {
      try {
        // Create a safer regex with a character limit
        const flags = caseSensitive ? 'g' : 'gi';
        const safeRegex = new RegExp(
          `${escapeRegExp(openPattern)}[\\s\\S]{0,1000}?${escapeRegExp(closePattern)}`,
          flags
        );
        processedCode += chunk.replace(safeRegex, '');
      } catch (e) {
        console.error('Error in regex processing:', e);
        processedCode += chunk; // Keep chunk unchanged if regex fails
      }
    }
  });

  return processedCode;
};

/**
 * Helper function to split text into manageable chunks
 * @param {string} text - The text to split
 * @param {number} size - The maximum chunk size
 * @returns {string[]} - Array of text chunks
 */
function splitIntoChunks(text, size) {
  const chunks = [];
  for (let i = 0; i < text.length; i += size) {
    chunks.push(text.slice(i, i + size));
  }
  return chunks;
}

/**
 * Helper function to escape special regex characters
 * @param {string} string - The string to escape
 * @returns {string} - Escaped string safe for regex
 */
function escapeRegExp(string) {
  if (!string || typeof string !== 'string') return '';
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Processes code with multiline pattern awareness
 * @param {string} code - The code to process
 * @param {Array} patterns - Array of pattern objects
 * @returns {string[]} - Array of lines that should not be ignored
 */
export const processCodeWithPatterns = (code, patterns = []) => {
  if (!code) return [];

  // First, preprocess the code to remove multiline sections
  const preprocessedCode = preprocessMultilinePatterns(code, patterns);

  // Split into lines and process
  const lines = preprocessedCode.split('\n');
  const result = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Skip empty lines
    if (line === '') continue;

    // Check if line should be ignored based on line patterns
    if (lineMatchesPattern(line, patterns)) continue;

    // If we got here, the line should be included
    result.push(line);
  }

  return result;
};

/**
 * Synchronous version of splitCodeIntoLines - for backward compatibility
 * @param {string} code - The code to split
 * @param {string} language - The programming language
 * @param {string[]} ignoreKeywords - Array of keywords to ignore
 * @returns {string[]} - Array of code lines
 */
export const splitCodeIntoLines = (code, language = 'JavaScript', ignoreKeywords) => {
  const resolvedLanguage = resolveCommentProcessingLanguage(code, language);
  const cleanedCode = stripComments(code, resolvedLanguage);

  const rawPatterns =
    ignoreKeywords == null
      ? getBuiltinIgnorePatternsForLanguage(resolvedLanguage)
      : ignoreKeywords;

  const patterns = Array.isArray(rawPatterns) && rawPatterns.length && typeof rawPatterns[0] === 'string'
    ? rawPatterns.map(k => ({ type: 'simple', pattern: k, caseSensitive: false, enabled: true }))
    : rawPatterns;

  // Preprocess to handle multiline patterns
  const preprocessedCode = preprocessMultilinePatterns(cleanedCode, patterns);

  // Split into lines and filter out empty lines, comment-only lines, and lines with ignore keywords
  return preprocessedCode
    .split('\n')
    .map(line => line.trim())
    .filter(line => {
      if (line === '') return false;
      // Only filter out lines starting with # for languages that use # for comments (not C++)
      if (line.startsWith('#') && usesHashComments(resolvedLanguage)) return false;
      if (line.startsWith('"""') || line.startsWith("'''")) return false;
      if (line.startsWith('//')) return false;
      if (line.startsWith('/*') || line.startsWith('*/')) return false;
      if (lineMatchesPattern(line, patterns)) return false;
      return true;
    });
};

/**
 * Asynchronous version of splitCodeIntoLines - for fetching language-specific patterns
 * @param {string} code - The code to split
 * @param {string} language - The programming language
 * @param {string} userId - The user ID (needed to fetch language-specific patterns)
 * @returns {Promise<string[]>} - Promise resolving to array of code lines
 */
export const splitCodeIntoLinesAsync = async (code, language = 'JavaScript', userId = null) => {
  try {
    const resolvedLanguage = resolveCommentProcessingLanguage(code, language);
    console.log('splitCodeIntoLinesAsync called with:', { language: resolvedLanguage, userId });

    const cleanedCode = stripComments(code, resolvedLanguage);
    console.log('Cleaned code length:', cleanedCode.length);

    let patterns;
    try {
      patterns = await getIgnorePatterns(userId, resolvedLanguage);
      console.log('Fetched patterns:', patterns.length);
    } catch (patternError) {
      console.error('Error fetching patterns, using built-in fallbacks:', patternError);
      patterns = getBuiltinIgnorePatternsForLanguage(resolvedLanguage);
    }

    // Preprocess to handle multiline patterns
    const preprocessedCode = preprocessMultilinePatterns(cleanedCode, patterns);
    console.log('Preprocessed code length:', preprocessedCode.length);

    // Process code with pattern awareness
    const result = processCodeWithPatterns(preprocessedCode, patterns);
    console.log('Processed result lines:', result.length);

    // For backward compatibility, also filter out comment-only lines
    const filteredResult = result.filter(line => {
      if (line.startsWith('#') && usesHashComments(resolvedLanguage)) return false;
      if (line.startsWith('"""') || line.startsWith("'''")) return false;
      if (line.startsWith('//')) return false;
      if (line.startsWith('/*') || line.startsWith('*/')) return false;
      return true;
    });

    console.log('Final filtered result lines:', filteredResult.length);
    return filteredResult;
  } catch (error) {
    console.error('Error in splitCodeIntoLinesAsync:', error);
    // Fall back to simple processing if there's an error
    console.log('Falling back to simple processing');
    return simpleCodeProcessing(code, resolveCommentProcessingLanguage(code, language));
  }
};

/**
 * For backward compatibility - retrieves user's ignore keywords using the old format
 * @param {string} userId - The user ID
 * @param {string} language - The programming language
 * @returns {Promise<string[]>} - Array of ignore keywords for the specified language
 */
export const getIgnoreKeywords = async (userId, language = 'JavaScript') => {
  try {
    // Get patterns in the new format
    const patterns = await getIgnorePatterns(userId, language);

    // Convert to the old format
    return patternsToKeywords(patterns);
  } catch (error) {
    console.error('Error fetching ignore keywords:', error);
    return DEFAULT_IGNORE_KEYWORDS;
  }
};

/**
 * Simple fallback function for code processing when Firebase is not available
 * @param {string} code - The code to process
 * @param {string} language - The programming language
 * @returns {string[]} - Array of code lines
 */
export const simpleCodeProcessing = (code, language = 'JavaScript') => {
  if (!code) return [];

  const resolvedLanguage = resolveCommentProcessingLanguage(code, language);
  let cleanedCode = preprocessLineComments(code, resolvedLanguage);
  if (resolvedLanguage === 'Python') {
    cleanedCode = stripPythonTripleQuotedDocstringsOnly(cleanedCode);
  }
  const patterns = getCommentPatterns(resolvedLanguage).filter(
    (pattern) => !shouldSkipCommentRegexPattern(pattern, resolvedLanguage)
  );
  cleanedCode = patterns.reduce((processed, pattern) => processed.replace(pattern, ''), cleanedCode);

  return cleanedCode
    .split('\n')
    .map(line => line.trim())
    .filter(line => {
      if (line === '') return false;
      if (line.startsWith('//') || line.startsWith('/*') || line.startsWith('*/')) return false;
      if (line.startsWith('#') && usesHashComments(resolvedLanguage)) return false;
      if (line.startsWith('"""') || line.startsWith("'''")) return false;
      return true;
    });
};

/**
 * Simple fallback function for code normalization when Firebase is not available
 * @param {string} code - The code to normalize
 * @param {string} language - The programming language
 * @returns {string} - Normalized code
 */
export const simpleNormalizeCode = (code, language = 'JavaScript') => {
  if (!code) return '';

  // Simple normalization without Firebase dependencies
  return code
    .replace(/\s+/g, '') // Remove all whitespace
    .replace(/function\s+([a-zA-Z_$][a-zA-Z0-9_$]*)/g, 'function') // Normalize function declarations
    .replace(/(?:let|const|var)\s+([a-zA-Z_$][a-zA-Z0-9_$]*)/g, 'let') // Normalize variable declarations
    .replace(/;/g, '') // Remove semicolons
    .replace(/\(\)/g, '') // Remove empty parentheses
    .toLowerCase(); // Convert to lowercase
};
